import React from 'react';

import { normalizeOptionalText } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import {
    formatExpectedBbd,
    formatExpectedQtySummary,
    formatExpectedUnitsPerCase,
    formatReceivedBbd,
    formatReceivedQtySummary,
    formatReceivedUnitsPerCase,
} from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineDisplayUtils';
import { getFcTransferReceiveLineBadgeClassName } from 'components/warehouse/components/fcTransfers/fcTransferReceiveBadgeStyles';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { TableCell, TableRow } from 'shadcn/components/ui/Table';

/**
 * Read-only accept-transfer line row + row-level actions (receive/edit open modal).
 *
 * @param {Object} props
 * @param {Record<string, unknown>} props.item
 * @param {boolean} [props.showReceive=false]
 * @param {boolean} [props.showEdit=false]
 * @param {boolean} [props.showPrintPallet=false]
 * @param {boolean} [props.showPrintCase=false]
 * @param {boolean} [props.actionsDisabled=false]
 * @param {() => void} [props.onReceive]
 * @param {() => void} [props.onEdit]
 */
export function AcceptTransferLineRow({
    actionsDisabled = false,
    item,
    onEdit,
    onReceive,
    showEdit = false,
    showPrintCase = false,
    showPrintPallet = false,
    showReceive = false,
}) {
    const lotText = normalizeOptionalText(item?.lotId ?? item?.lot_id);
    const skuText = normalizeOptionalText(item?.externalSku ?? item?.external_sku);
    const productText = normalizeOptionalText(item?.itemName ?? item?.item_name);
    const statusRaw = item?.status;
    const statusLabel = normalizeOptionalText(statusRaw);
    const statusClass = getFcTransferReceiveLineBadgeClassName(statusRaw);

    return (
        <TableRow data-slot="accept-transfer-line-row">
            <TableCell className="text-left align-top whitespace-nowrap">{lotText}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{skuText}</TableCell>
            <TableCell className="text-left align-top break-words">{productText}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatExpectedQtySummary(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatReceivedQtySummary(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatExpectedBbd(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatReceivedBbd(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatExpectedUnitsPerCase(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">{formatReceivedUnitsPerCase(item)}</TableCell>
            <TableCell className="text-left align-top whitespace-nowrap">
                <Badge variant="outline" className={statusClass}>
                    {statusLabel}
                </Badge>
            </TableCell>
            <TableCell className="text-left align-top">
                <div className="flex min-w-[12rem] flex-wrap gap-2">
                    {showReceive ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={actionsDisabled}
                            onClick={onReceive}
                        >
                            Receive
                        </Button>
                    ) : null}
                    {showEdit ? (
                        <Button type="button" size="sm" variant="secondary" disabled={actionsDisabled} onClick={onEdit}>
                            Edit
                        </Button>
                    ) : null}
                    {showPrintPallet ? (
                        <Button type="button" size="sm" variant="outline" disabled>
                            Print pallet
                        </Button>
                    ) : null}
                    {showPrintCase ? (
                        <Button type="button" size="sm" variant="outline" disabled>
                            Print case
                        </Button>
                    ) : null}
                </div>
            </TableCell>
        </TableRow>
    );
}

AcceptTransferLineRow.displayName = 'AcceptTransferLineRow';
